/**
 * Genesis design system v2 — token namespace.
 *
 * Single canonical export `theme` matching the agentis2 prototype
 * (http://localhost:8000/) end-to-end. Every value here is **measured**
 * from the prototype — see `docs/specs/ds-rebuild/01-foundations.md`
 * for screenshot evidence per token.
 *
 * This file is **additive**: the legacy `color` / `typography` / `space` /
 * etc. exports in this folder remain in place to keep existing components
 * compiling. Layer 2 of the DS rebuild migrates every component to the
 * `theme` namespace; at the end of Layer 5 the legacy tokens get removed.
 *
 * Import as: `import { theme } from '@agentisai/design-system';`
 *
 * Sources of ground truth (do not invent values; if a token is missing,
 * extract it from the prototype with `getComputedStyle()` first):
 *
 *   - Live app:        http://localhost:8000/   (canonical visual)
 *   - DS doc:          /Users/shafiek/Downloads/agentis2/genesis-design-system.html
 *   - Captured states: .playwright-mcp/jaco-*.png, store.png, insights.png, etc.
 */

/* ─────────────────────────────────────────────────────────────────────
 * Color
 * ──────────────────────────────────────────────────────────────────── */

const neutral = {
  /** #0f172a — primary dark. Icon-rail bg, document covers, table headers, section number pills. */
  dark: '#0f172a',
  /** #1e3a5f — primary navy. Active rail-button bg, document cover gradient end, workstream context icons. */
  navy: '#1e3a5f',
  /** #4b5563 — primary buttons (Login, "Send Email", "Confirm Trust"); human avatar bg; active workstream filter. */
  charcoal: '#4b5563',
  /** #1f2937 — product body text (headings, strong). */
  body: '#1f2937',
  /** #1e293b — document body text (functionally identical to `body`; document-side variant). */
  bodyDoc: '#1e293b',
  /** #374151 — product secondary. */
  secondary: '#374151',
  /** #475569 — document body paragraphs, message-bubble text. */
  secondaryDoc: '#475569',
  /** #6b7280 — metadata, timestamps, helper text, inactive nav links. */
  muted: '#6b7280',
  /** #64748b — document cover meta-label colour (slate-500). Validated 2026-04-30 against rendered prototype. */
  slate: '#64748b',
  /** #9ca3af — placeholders, section labels, disabled. */
  faint: '#9ca3af',
  /** #94a3b8 — document section labels, doc cover eyebrow, inactive rail-button icons. */
  fainter: '#94a3b8',
  /** #e5e7eb — product card borders, panel dividers, table-row separators. */
  border: '#e5e7eb',
  /** #e2e8f0 — document borders. */
  borderDoc: '#e2e8f0',
  /** #d1d5db — input borders, ghost-button borders, off-state Switch track. */
  borderStrong: '#d1d5db',
  /** #f1f5f9 — slate-100 raised surface (memory/agent cards, stat chips on the rail panel). Cooler than `bgCanvas`; sits on a white panel. */
  bgRaised: '#f1f5f9',
  /** #cbd5e1 — slate-300 strong divider / ghost-button border on document-tinted surfaces (rail-panel memory "View" control, logo lockup gradient start). Cooler + heavier than `borderStrong`. */
  divider: '#cbd5e1',
  /** #f3f4f6 — page background, hover background, inactive pill bg. */
  bgCanvas: '#f3f4f6',
  /** #f9fafb — chat / canvas bg, input bg, hover surface. */
  bgSurface: '#f9fafb',
  /** #f8fafc — document page bg. */
  bgPage: '#f8fafc',
  /** #ffffff — white card surfaces. */
  white: '#ffffff',
} as const;

const accent = {
  /** Specialist agent ring, links, focus ring. Tailwind blue family. */
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    /** Soft-fill button border (Send to Client, On it, Switch to...). Validated 2026-04-30 against rendered prototype — was missing in v0 of the theme. */
    200: '#bfdbfe',
    300: '#93c5fd',
    500: '#3b82f6',
    700: '#1e40af',
  },
  /**
   * Above-the-loop trust, success, sync. Mixed Tailwind family:
   *   50 / 200 are Tailwind GREEN (used for the Trust button shell);
   *   100 / 300 / 500 / 700 are Tailwind EMERALD (used for trust pills,
   *   Synced badge, status dots, system signals).
   * The prototype mixes them; we follow. The `200` step (`#bbf7d0`) is
   * the "Trust" approval-button border. Trust button hover (`#dcfce7`,
   * Tailwind green-100, distinct from the emerald `100` here) is
   * inlined in `Button.css.ts` because it's a button-specific state
   * rather than a reusable token.
   */
  green: {
    50: '#f0fdf4',
    100: '#d1fae5',
    /** #bbf7d0 — "Trust" approval-button border. Validated 2026-04-30 against `.approval-btn-trust`. */
    200: '#bbf7d0',
    300: '#6ee7b7',
    500: '#10b981',
    700: '#065f46',
  },
  /** In-the-loop trust, pending, warning. Tailwind amber family. */
  amber: {
    /** Document warning callout bg. Validated 2026-04-30 against `genesis-prd.html` `.callout-amber`. */
    50: '#fffbeb',
    100: '#fef3c7',
    300: '#fcd34d',
    500: '#f59e0b',
    700: '#92400e',
  },
  /** Agent Smith / Mira (system agents). Tailwind purple/violet family. */
  purple: {
    50: '#faf5ff',
    300: '#a78bfa',
    500: '#8b5cf6',
    700: '#6d28d9',
    900: '#4c1d95',
  },
  /** Errors, "NEEDS YOUR ATTENTION" badge, rollback. Tailwind red family. */
  red: {
    50: '#fef2f2',
    100: '#fecaca',
    500: '#ef4444',
    700: '#991b1b',
  },
  /** Workstream Agent (WA), coordination layer, active artifact tint. Tailwind teal family. */
  teal: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    300: '#99f6e4',
    500: '#14b8a6',
    700: '#0f766e',
  },
} as const;

const status = {
  dot: {
    idle: neutral.borderStrong,
    routine: neutral.muted,
    active: accent.green[500],
    warn: accent.amber[500],
    danger: accent.red[500],
    info: accent.blue[500],
  },
} as const;

const overlay = {
  /** Modal backdrop. Measured on `jaco-modal-send-to-client.png`. */
  modalBackdrop: 'rgba(0, 0, 0, 0.35)',
} as const;

const shadow = {
  /** Profile menu, focus dropdown, focus-switcher. */
  dropdown: '0 8px 24px rgba(0, 0, 0, 0.12)',
  /** Centered modal (Send to Client, Trust Confirm, etc.). */
  modal: '0 16px 48px rgba(0, 0, 0, 0.12)',
  /** Card hover lift. */
  cardHover: '0 2px 8px rgba(0, 0, 0, 0.08)',
  /** Elevated card / promotion card. */
  cardElevated: '0 4px 16px rgba(0, 0, 0, 0.08)',
  /** Switch knob inner shadow. */
  knob: '0 1px 3px rgba(0, 0, 0, 0.2)',
  /**
   * TrustBadge "engraved-lamp" inset highlight — a subtle 1px inset of
   * the active tick's own hue at 20% alpha (the `…700` step). One per
   * trust level. Pre-resolved rgba so the value survives a future
   * tokens-as-CSS-vars migration (string-concatenating a hex `33` alpha
   * onto a token colour would break the moment the token becomes a
   * `var(--…)` reference).
   *   - inLoop  = `accent.amber[700]` (#92400e) @ 20%
   *   - onLoop  = `accent.blue[700]`  (#1e40af) @ 20%
   *   - aboveLoop = `accent.green[700]` (#065f46) @ 20%
   */
  trustInsetInLoop: 'inset 0 1px 0 rgba(146, 64, 14, 0.2)',
  trustInsetOnLoop: 'inset 0 1px 0 rgba(30, 64, 175, 0.2)',
  trustInsetAboveLoop: 'inset 0 1px 0 rgba(6, 95, 70, 0.2)',
  /** No shadow (explicit value, used by panels and other flat surfaces). */
  none: 'none',
} as const;

const focus = {
  /** Default keyboard-focus ring — accent.blue at 20% over a 3px outset. */
  ring: '0 0 0 3px rgba(59, 130, 246, 0.20)',
  /** Danger / destructive controls. */
  ringDanger: '0 0 0 3px rgba(239, 68, 68, 0.25)',
  /** Agent-themed surfaces. */
  ringAgent: '0 0 0 3px rgba(167, 139, 250, 0.30)',
  /**
   * Selection halo — 2px `accent.blue[500]` glow at 15% marking a
   * *selected* (not keyboard-focused) surface, e.g. the active artifact
   * card in the rail panel. Distinct from `ring`: thinner (2px) and
   * fainter (15%), persists while selected rather than only on
   * `:focus-visible`.
   */
  selectionHalo: '0 0 0 2px rgba(59, 130, 246, 0.15)',
} as const;

/**
 * Tenant-avatar tints encoded from `index.html` `selectLoginUser`.
 * Application-level data; exposed here as a documented preset so
 * stories and previews can render the canonical demo users without
 * re-declaring the values.
 */
const tenant = {
  /** Soren Voss · Agentis Ai */
  soren: '#4b5563',
  /** Jaco Vorster · BG Private */
  jaco: '#1e40af',
  /** Daniel Carter · StratBridge Advisory */
  daniel: '#065f46',
  /** Sophie Laurent · Ashworth Cole LLP */
  sophie: '#7c3aed',
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Typography
 * ──────────────────────────────────────────────────────────────────── */

const fontFamily = {
  /** System sans stack — no Fraunces, no IBM Plex. Matches the prototype's
   * `body { font-family: ... }` declaration verbatim. */
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  /** Monospace fallback (no observed prototype use yet; reserved). */
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

const fontSize = {
  /** 9px — inline "Agent" / "System Agent" header badge next to message-bubble sender name. */
  micro: '9px',
  /** 10px — uppercase tab counts, decision-tag-gov, trust pills, attention count badge. */
  nano: '10px',
  /** 11px — pill text, badge text, metadata, doc-cover meta-label, attention-card buttons. */
  tiny: '11px',
  /** 12px — filter pills, helper text, sidebar muted text, KPI label. */
  caption: '12px',
  /** 13px — default body, message bubble, list items, store-card description. */
  bodySm: '13px',
  /** 14px — product body, large sidebar list, login button, primary button. */
  body: '14px',
  /** 15px — profile name, doc body, store-card title. */
  bodyLg: '15px',
  /** 16px — workstream header, modal-card title, AgentForge h1. */
  titleSm: '16px',
  /** 17px — document cover subtitle. */
  title: '17px',
  /** 18px — login-card title, modal-header title. */
  titleLg: '18px',
  /** 22px — page header h1, document section title. */
  h2: '22px',
  /** 38px — document cover title (PRD, Audit, DS doc, etc.). */
  docTitle: '38px',
} as const;

const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  /** 800 — document cover title only. */
  heavy: 800,
} as const;

const lineHeight = {
  /** 1.1 — document title, KPI value. */
  tight: 1.1,
  /** 1.3 — sidebar items, action-card title. */
  compact: 1.3,
  /** 1.5 — product body. */
  body: 1.5,
  /** 1.6 — message bubble body, callout body. */
  relaxed: 1.6,
  /** 1.7 — document paragraphs (PRD, Audit, etc.). */
  docBody: 1.7,
} as const;

const letterSpacing = {
  /** -1px — document cover title at 38px only. */
  tight: '-1px',
  /** -0.5px — brand line "Agentis Ai" lockup. */
  tightish: '-0.5px',
  /** -0.3px — document section titles (22px h2). Validated 2026-04-30. */
  sectionTitle: '-0.3px',
  normal: '0',
  /** 0.5px — uppercase section labels (small). */
  label: '0.5px',
  /** 1px — document metadata labels, document TOC title. */
  docLabel: '1px',
  /** 3px — small "GENESIS" lockup line (top-nav size). */
  brand: '3px',
  /** 4px — large "GENESIS" lockup line (login screen). */
  brandLg: '4px',
  /** 1.5px — document TOC title only. */
  docToc: '1.5px',
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Spacing
 *
 * Named scale supporting every measured value from the prototype's inline
 * styles. The histogram (top values: 12 / 8 / 10 / 4 / 1 / 6 / 14 / 11 /
 * 13 / 16 px, plus 3 / 5 / 7 / 9 / 18 / 28 / 36 px) drives this set.
 *
 * Numeric keys (`space[3]` = 12) preserve the existing component idiom.
 * Named keys (`space.cardPadInner` = 14) document semantic uses.
 *
 * Off-scale values (3, 5, 7, 9, 11, 13, 15, 17, 18, 36, etc.) are
 * intentionally NOT tokens — use a raw px literal in `.css.ts` with a
 * `// optical micro-tweak: <reason>` comment immediately above (per
 * tokenUsage.test.tsx).
 * ──────────────────────────────────────────────────────────────────── */

const space = {
  0: '0',
  /** 1px — borders, dividers, inline-badge padding-y. */
  px: '1px',
  /** 2px — inline-with-icon micro alignment. */
  '0_5': '2px',
  /** 3px — pill padding-y (filter pills, trust pills, Verified badge). Systemic; not a tweak. */
  '0_75': '3px',
  /** 4px */
  1: '4px',
  /** 5px — soft-fill button padding-y (On it, Switch to, Send to Client, Stay in HBG). Systemic; not a tweak. */
  '1_25': '5px',
  /** 6px — attention count badge padding-x, dot gap. */
  '1_5': '6px',
  /** 7px — observed in restore-stats / approval-card-header padding-y. */
  '1_75': '7px',
  /** 8px */
  2: '8px',
  /** 10px — icon margin, message gap, small card padding. */
  '2_5': '10px',
  /** 12px — card body, list-item vertical, message-bubble padding-y. */
  3: '12px',
  /** 13px — login button padding-y (the only place 13px is observed; isolated systemic use). */
  '3_25': '13px',
  /** 14px — message-bubble padding-x, card padding inner. */
  '3_5': '14px',
  /** 16px — card outer padding, message-list gap, modal-body padding-y. */
  4: '16px',
  /** 20px — card padding-x outer, modal-body padding-x. */
  5: '20px',
  /** 24px — section padding, top-nav padding, modal-header padding. */
  6: '24px',
  /** 28px — document section spacing seed. */
  7: '28px',
  /** 32px — page padding (forge / store / insights), section margin-bottom. */
  8: '32px',
  /** 40px */
  10: '40px',
  /** 48px — document padding-top, rail width. */
  12: '48px',
  /** 56px — top-nav height. */
  14: '56px',
  /** 64px */
  16: '64px',
  /** 96px — document page padding-bottom. */
  24: '96px',
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Control height
 *
 * Shared nominal heights for the action-control family (Button +
 * IconButton) so a `size="md"` Button and a `size="md"` IconButton line
 * up to the pixel inside a toolbar / button group. Before this token
 * existed the two drifted (Button md ≈ 30px padding-driven vs IconButton
 * md = 36px fixed-box → a 6px mismatch).
 *
 * The scale reconciles the two prototype-measured families onto one:
 *   - sm 30 — IconButton was 32 (composer paperclip/lightning/send);
 *             Button sm was a padding-driven ~25. 30 splits them and
 *             matches the Input `sm` (search) box, so a small Button,
 *             IconButton and search Input share a row cleanly.
 *   - md 36 — IconButton's measured top-nav notify-bell / profile-menu
 *             box, unchanged. Button md (was padding-driven ~30) snaps up
 *             to it.
 *   - lg 44 — the WCAG 2.5.5 / coarse-pointer 44px target. IconButton lg
 *             was already 44; Button lg (was ~43) rounds to it.
 *
 * Text fields (Input / Textarea / Select) keep their own taller scale
 * (30 / 38 / 48) — those heights are individually prototype-grounded to
 * the login / modal-field / search surfaces and a field sitting flush
 * with a button is not a layout the prototype expresses, so forcing them
 * onto this scale would regress login + modal visuals for no gain.
 * ──────────────────────────────────────────────────────────────────── */

const controlHeight = {
  /** 30px — small Button / IconButton (composer, dense toolbars). */
  sm: 30,
  /** 36px — default Button / IconButton (top-nav chrome, modal footer). */
  md: 36,
  /** 44px — large Button / IconButton; meets the WCAG 2.5.5 coarse-pointer target. */
  lg: 44,
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Radius
 * ──────────────────────────────────────────────────────────────────── */

const radii = {
  /** 2px — count-badges corners, decision-tag-icon. */
  sharp: '2px',
  /** 4px — tag chips, count badges, status pills. */
  xs: '4px',
  /** 6px — compact buttons, action-status-btn, filter-bar action button. */
  sm: '6px',
  /** 8px — inputs, ghost buttons, rail-button (when active), top-nav notify-btn. */
  md: '8px',
  /** 10px — small card, message bubble, restore-point card, focus dropdown. */
  lg: '10px',
  /** 12px — card, modal, login-card, store-card, KPI card. */
  xl: '12px',
  /** 14px — modal-card legacy variant, wizard footer. */
  x2l: '14px',
  /** 16px — Create-Workstream wizard, document cover. */
  x3l: '16px',
  /** 20px — pills (filter, trust, count badges), store-card chips. */
  pill: '20px',
  /** 50% — exact value the prototype uses for circular avatars / status dots / online dot. Functionally equivalent to `full` (9999px) for square boxes; documented as a separate token because the prototype literally writes `border-radius: 50%`. */
  circle: '50%',
  /** 9999px — fallback "fully rounded" for non-square shapes (still resolves to a perfect circle on square boxes). */
  full: '9999px',
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Sizing
 *
 * Named max-widths for content surfaces whose width is a deliberate
 * readability constraint rather than a layout dimension. Distinct from
 * `space` (gaps / padding) — these bound a measure of text/content.
 * ──────────────────────────────────────────────────────────────────── */

const sizing = {
  /**
   * 280px — max-width of small floating overlay surfaces (Tooltip bubble,
   * Popover/Menu body) so multi-line copy wraps to a comfortable measure
   * instead of stretching across the viewport.
   */
  overlayMaxWidth: '280px',
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Layout-shell dimensions
 *
 * Fixed structural dimensions of the workspace-shell surfaces (TopNav /
 * IconRail / RailPanel / ChatPanel). These are *named layout dimensions*,
 * not content max-widths (`sizing`) or control heights (`controlHeight`) —
 * the tokens literally name the use, so a contributor reads
 * `theme.size.topNavHeight` instead of a bare `56` and the dimension is
 * editable in one place. Numeric (unitless) where the consuming
 * vanilla-extract property accepts a number (e.g. `width`, `height`,
 * `minWidth`); kept as numbers (not `'56px'` strings) to match the rest of
 * the layout `.css.ts` idiom and the tokenUsage size-table convention.
 *
 * Prototype-grounded (`.playwright-mcp/jaco-01-workspace.png`):
 *   - top-nav 56px tall, icon-rail 48px wide, rail-panel 280px wide,
 *     chat-panel floors at 360px before it gets uncomfortably narrow.
 * ──────────────────────────────────────────────────────────────────── */

const size = {
  /** 56px — TopNav height (sticky app bar). */
  topNavHeight: 56,
  /** 48px — IconRail width (the dark left navigation column). */
  iconRailWidth: 48,
  /** 36px — IconRail button footprint (square hit-target inside the 48px rail). */
  iconRailButton: 36,
  /** 280px — RailPanel width (slide-out panel anchored to the IconRail). */
  railPanelWidth: 280,
  /** 360px — ChatPanel minimum width before its 40% flex-basis floors out. */
  chatPanelMinWidth: 360,
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Motion
 * ──────────────────────────────────────────────────────────────────── */

const motion = {
  duration: {
    /** 120ms — default UI transitions (button hover, badge state). */
    fast: '120ms',
    /** 200ms — modal open/close, dropdown reveal. */
    normal: '200ms',
    /** 400ms — focus dropdown reveal, slide-out panels. */
    slow: '400ms',
    /** 2s — Smith status dot, WA pulse-dot keyframe. */
    pulse: '2s',
  },
  easing: {
    /** Standard ease — every UI transition unless otherwise specified. */
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    /** Pulse keyframe easing. */
    pulse: 'ease-in-out',
  },
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Z-index
 * ──────────────────────────────────────────────────────────────────── */

const zIndex = {
  /** 100 — sticky top nav. */
  sticky: 100,
  /** 200 — profile menu, focus dropdown. */
  dropdown: 200,
  /**
   * 1050 — portalled floating surfaces (Popover, Menu). Sits *above* the
   * modal card (1010) so a popover/menu triggered from inside a Modal
   * renders over the modal content rather than behind it, while staying
   * below the toast tray (1100) and tooltip (1200) so status toasts and
   * hover hints remain visible on top of an open popover.
   * (Replaced the legacy `drawer` (500) token — the only borrower — which
   * left popovers stranded behind modals; Drawer/Sheet were deleted
   * 2026-05-02.)
   */
  popover: 1050,
  /** 1000 — modal backdrop. */
  modalBackdrop: 1000,
  /** 1010 — modal card on top of backdrop. */
  modal: 1010,
  /** 1100 — toast tray (above modal so error toasts during a modal flow are visible). */
  toast: 1100,
  /** 1200 — tooltip (always topmost). */
  tooltip: 1200,
} as const;

/* ─────────────────────────────────────────────────────────────────────
 * Public surface
 * ──────────────────────────────────────────────────────────────────── */

export const theme = {
  color: {
    neutral,
    accent,
    status,
    overlay,
    tenant,
  },
  typography: {
    fontFamily,
    fontSize,
    fontWeight,
    lineHeight,
    letterSpacing,
  },
  space,
  controlHeight,
  radii,
  sizing,
  size,
  shadow,
  focus,
  motion,
  zIndex,
} as const;

export type GenesisTheme = typeof theme;
